import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./AdminPanel/Sidebar";

const initialCreateForm = {
  tenant_name: "",
  admin_name: "",
  admin_email: "",
  admin_phone: "",
};

const initialEditForm = {
  tenantId: null,
  adminId: null,
  tenantName: "",
  tenantActive: true,
  adminName: "",
  adminEmail: "",
  adminPhone: "",
  adminActive: true,
};

export default function AdminTenant() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createForm, setCreateForm] = useState(
    initialCreateForm
  );
  const [editForm, setEditForm] = useState(
    initialEditForm
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const clearSession = useCallback(() => {
    localStorage.removeItem("admin_access_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_user");
  }, []);

  const showMessage = useCallback((text, type) => {
    setMessage(text);
    setMessageType(type);
  }, []);

  const apiRequest = useCallback(
    async (url, options = {}) => {
      const accessToken = localStorage.getItem(
        "admin_access_token"
      );

      if (!accessToken) {
        clearSession();
        navigate("/admin-panel/login", {
          replace: true,
        });
        throw new Error("La sesión ha finalizado.");
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
          ...(options.body
            ? { "Content-Type": "application/json" }
            : {}),
          ...(options.headers || {}),
        },
        cache: "no-store",
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.status === 401) {
        clearSession();
        navigate("/admin-panel/login", {
          replace: true,
        });
        throw new Error("La sesión ha finalizado.");
      }

      if (!response.ok || data.ok !== true) {
        throw new Error(
          data.message ||
            "No fue posible completar la solicitud."
        );
      }

      return data;
    },
    [
      clearSession,
      navigate,
    ]
  );

  const loadTenants = useCallback(async () => {
    setTableLoading(true);

    try {
      const data = await apiRequest(
        "/tenants/api/list/"
      );

      setTenants(
        Array.isArray(data.results)
          ? data.results
          : []
      );
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "No fue posible cargar las organizaciones.",
        "error"
      );
    } finally {
      setTableLoading(false);
    }
  }, [
    apiRequest,
    showMessage,
  ]);

  useEffect(() => {
    const initialize = async () => {
      const accessToken = localStorage.getItem(
        "admin_access_token"
      );

      const storedUser = localStorage.getItem("admin_user");

      if (!accessToken || !storedUser) {
        navigate("/admin-panel/login", {
          replace: true,
        });
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);

        if (
          parsedUser.user_type !== "SUPERADMIN" ||
          parsedUser.is_superuser !== true
        ) {
          throw new Error("No tienes acceso.");
        }

        setUser(parsedUser);
        await loadTenants();
      } catch (error) {
        clearSession();

        showMessage(
          error instanceof Error
            ? error.message
            : "No fue posible cargar la sesión.",
          "error"
        );

        window.setTimeout(() => {
          navigate("/admin-panel/login", {
            replace: true,
          });
        }, 1000);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [
    clearSession,
    loadTenants,
    navigate,
    showMessage,
  ]);

  const filteredTenants = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return tenants;
    }

    return tenants.filter((tenant) => {
      const values = [
        tenant.name,
        tenant.slug,
        tenant.admin?.name,
        tenant.admin?.email,
        tenant.admin?.phone,
      ];

      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(normalizedSearch)
      );
    });
  }, [search, tenants]);

  const paginatedTenants = useMemo(() => {
    const start = page * rowsPerPage;

    return filteredTenants.slice(
      start,
      start + rowsPerPage
    );
  }, [
    filteredTenants,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const handleCreateChange = (event) => {
    const { name, value } = event.target;

    setCreateForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;

    setEditForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleCreateTenant = async () => {
    const tenantName = createForm.tenant_name.trim();
    const adminName = createForm.admin_name.trim();
    const adminEmail = createForm.admin_email
      .trim()
      .toLowerCase();

    if (!tenantName || !adminName || !adminEmail) {
      showMessage(
        "Completa los campos obligatorios.",
        "error"
      );
      return;
    }

    setSaving(true);

    try {
      const data = await apiRequest(
        "/tenants/api/create/",
        {
          method: "POST",
          body: JSON.stringify({
            tenant_name: tenantName,
            admin_name: adminName,
            admin_email: adminEmail,
            admin_phone:
              createForm.admin_phone.trim(),
          }),
        }
      );

      setCreateOpen(false);
      setCreateForm(initialCreateForm);

      showMessage(
        data.message ||
          "Organización creada correctamente.",
        "success"
      );

      await loadTenants();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "No fue posible crear la organización.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (tenant) => {
    setEditForm({
      tenantId: tenant.id,
      adminId: tenant.admin?.id || null,
      tenantName: tenant.name || "",
      tenantActive: tenant.is_active === true,
      adminName: tenant.admin?.name || "",
      adminEmail: tenant.admin?.email || "",
      adminPhone: tenant.admin?.phone || "",
      adminActive:
        tenant.admin?.is_active === true,
    });

    setEditOpen(true);
  };

  const handleUpdateTenant = async () => {
    const tenantName = editForm.tenantName.trim();
    const adminName = editForm.adminName.trim();
    const adminEmail = editForm.adminEmail
      .trim()
      .toLowerCase();

    if (!tenantName) {
      showMessage(
        "El nombre de la organización es obligatorio.",
        "error"
      );
      return;
    }

    if (
      editForm.adminId &&
      (!adminName || !adminEmail)
    ) {
      showMessage(
        "El nombre y correo del administrador son obligatorios.",
        "error"
      );
      return;
    }

    setSaving(true);

    try {
      await apiRequest(
        `/tenants/api/${editForm.tenantId}/`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: tenantName,
            is_active: editForm.tenantActive,
          }),
        }
      );

      if (editForm.adminId) {
        await apiRequest(
          `/tenants/api/admins/${editForm.adminId}/`,
          {
            method: "PATCH",
            body: JSON.stringify({
              name: adminName,
              email: adminEmail,
              phone: editForm.adminPhone.trim(),
              is_active: editForm.adminActive,
            }),
          }
        );
      }

      setEditOpen(false);
      setEditForm(initialEditForm);

      showMessage(
        "Organización actualizada correctamente.",
        "success"
      );

      await loadTenants();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "No fue posible actualizar la organización.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editForm.adminId) {
      return;
    }

    setSaving(true);

    try {
      const data = await apiRequest(
        `/tenants/api/admins/${editForm.adminId}/reset-password/`,
        {
          method: "POST",
        }
      );

      showMessage(
        data.message ||
          "Contraseña restablecida correctamente.",
        "success"
      );
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "No fue posible restablecer la contraseña.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleViewUsers = (tenantId) => {
    navigate(
      `/admin-panel/tenants/${tenantId}/usuarios`
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1500,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: {
                    xs: "inline-flex",
                    md: "none",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Typography
                component="h1"
                sx={{
                  color: "#111111",
                  fontSize: {
                    xs: 25,
                    md: 31,
                  },
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
              >
                Organizaciones
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => setCreateOpen(true)}
              disableElevation
              sx={{
                minHeight: 42,
                borderRadius: 1.5,
                backgroundColor: "#000000",
                color: "#ffffff",
                fontSize: 13,
                fontWeight: 800,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#272727",
                },
              }}
            >
              Nueva organización
            </Button>
          </Box>

          <Box
            sx={{
              marginBottom: 2,
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Buscar organización o administrador"
              sx={{
                maxWidth: 520,
                backgroundColor: "#ffffff",
              }}
            />
          </Box>

          <TableContainer
            sx={{
              backgroundColor: "#ffffff",
              border: "1px solid #d1d5db",
              overflowX: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f9fafb",
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Organización
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Administrador
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Correo
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Teléfono
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Usuarios
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Estado
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tableLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ paddingY: 6 }}
                    >
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : paginatedTenants.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        paddingY: 6,
                        color: "#6b7280",
                        fontSize: 14,
                        fontWeight: 700,
                      }}
                    >
                      No existen organizaciones registradas.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTenants.map((tenant) => (
                    <TableRow
                      key={tenant.id}
                      hover
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            color: "#111111",
                            fontSize: 14,
                            fontWeight: 800,
                          }}
                        >
                          {tenant.name}
                        </Typography>

                        <Typography
                          sx={{
                            marginTop: 0.3,
                            color: "#6b7280",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          {tenant.slug}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        {tenant.admin?.name ||
                          "Sin administrador"}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {tenant.admin?.email || "—"}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {tenant.admin?.phone || "—"}
                      </TableCell>

                      <TableCell align="center">
                        <Button
                          variant="text"
                          onClick={() =>
                            handleViewUsers(tenant.id)
                          }
                          sx={{
                            minWidth: 0,
                            color: "#111111",
                            fontSize: 13,
                            fontWeight: 800,
                            textTransform: "none",
                          }}
                        >
                          {tenant.users_count || 0}
                        </Button>
                      </TableCell>

                      <TableCell align="center">
                        <Typography
                          component="span"
                          sx={{
                            display: "inline-block",
                            minWidth: 72,
                            paddingX: 1.2,
                            paddingY: 0.5,
                            border: "1px solid",
                            borderColor: tenant.is_active
                              ? "#bbf7d0"
                              : "#fecaca",
                            backgroundColor: tenant.is_active
                              ? "#f0fdf4"
                              : "#fef2f2",
                            color: tenant.is_active
                              ? "#166534"
                              : "#b91c1c",
                            fontSize: 11,
                            fontWeight: 800,
                            textAlign: "center",
                          }}
                        >
                          {tenant.is_active
                            ? "Activa"
                            : "Inactiva"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() =>
                            openEditDialog(tenant)
                          }
                          sx={{
                            minHeight: 34,
                            borderRadius: 1.5,
                            borderColor: "#111111",
                            color: "#111111",
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: "none",
                            "&:hover": {
                              borderColor: "#111111",
                              backgroundColor: "#f3f4f6",
                            },
                          }}
                        >
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={filteredTenants.length}
              page={page}
              onPageChange={(_, newPage) =>
                setPage(newPage)
              }
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(
                  Number(event.target.value)
                );
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="Filas"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </TableContainer>
        </Box>
      </Box>

      <Dialog
        open={createOpen}
        onClose={() => {
          if (!saving) {
            setCreateOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 1.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 800,
          }}
        >
          Nueva organización
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              paddingTop: 1,
            }}
          >
            <TextField
              label="Nombre de la organización"
              name="tenant_name"
              value={createForm.tenant_name}
              onChange={handleCreateChange}
              fullWidth
              required
              size="small"
            />

            <TextField
              label="Nombre del administrador"
              name="admin_name"
              value={createForm.admin_name}
              onChange={handleCreateChange}
              fullWidth
              required
              size="small"
            />

            <TextField
              label="Correo del administrador"
              name="admin_email"
              type="email"
              value={createForm.admin_email}
              onChange={handleCreateChange}
              fullWidth
              required
              size="small"
            />

            <TextField
              label="Teléfono"
              name="admin_phone"
              value={createForm.admin_phone}
              onChange={handleCreateChange}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            paddingX: 3,
            paddingBottom: 2.5,
          }}
        >
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={saving}
            sx={{
              color: "#4b5563",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleCreateTenant}
            disabled={saving}
            disableElevation
            sx={{
              backgroundColor: "#000000",
              fontWeight: 800,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#272727",
              },
            }}
          >
            {saving ? "Guardando" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => {
          if (!saving) {
            setEditOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 1.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 800,
          }}
        >
          Editar organización
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              paddingTop: 1,
            }}
          >
            <TextField
              label="Nombre de la organización"
              name="tenantName"
              value={editForm.tenantName}
              onChange={handleEditChange}
              fullWidth
              required
              size="small"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.tenantActive}
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      tenantActive:
                        event.target.checked,
                    }))
                  }
                />
              }
              label="Organización activa"
            />

            <Box
              sx={{
                height: 1,
                backgroundColor: "#e5e7eb",
              }}
            />

            <TextField
              label="Nombre del administrador"
              name="adminName"
              value={editForm.adminName}
              onChange={handleEditChange}
              fullWidth
              required={Boolean(editForm.adminId)}
              disabled={!editForm.adminId}
              size="small"
            />

            <TextField
              label="Correo del administrador"
              name="adminEmail"
              type="email"
              value={editForm.adminEmail}
              onChange={handleEditChange}
              fullWidth
              required={Boolean(editForm.adminId)}
              disabled={!editForm.adminId}
              size="small"
            />

            <TextField
              label="Teléfono"
              name="adminPhone"
              value={editForm.adminPhone}
              onChange={handleEditChange}
              fullWidth
              disabled={!editForm.adminId}
              size="small"
            />

            {editForm.adminId ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editForm.adminActive}
                      onChange={(event) =>
                        setEditForm((current) => ({
                          ...current,
                          adminActive:
                            event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Administrador activo"
                />

                <Button
                  variant="outlined"
                  onClick={handleResetPassword}
                  disabled={saving}
                  sx={{
                    justifySelf: "flex-start",
                    borderColor: "#b00020",
                    color: "#b00020",
                    fontWeight: 800,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#92001a",
                      backgroundColor: "#fff1f2",
                    },
                  }}
                >
                  Restablecer contraseña
                </Button>
              </>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            paddingX: 3,
            paddingBottom: 2.5,
          }}
        >
          <Button
            onClick={() => setEditOpen(false)}
            disabled={saving}
            sx={{
              color: "#4b5563",
              fontWeight: 800,
              textTransform: "none",
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleUpdateTenant}
            disabled={saving}
            disableElevation
            sx={{
              backgroundColor: "#000000",
              fontWeight: 800,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#272727",
              },
            }}
          >
            {saving ? "Guardando" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={4000}
        onClose={() => setMessage("")}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={messageType}
          onClose={() => setMessage("")}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}